import { useState } from "react";
import { Link } from "wouter";
import { ChevronRight, MessageCircle, MessagesSquare, PenSquare } from "lucide-react";
import type { ClubAPI } from "@shared/types/api";
import { useClubPosts } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { ClubAuthorAvatar } from "./ClubAuthorAvatar";
import { ClubNewPostSheet } from "./ClubNewPostSheet";
import { GateNotice } from "./clubComponents";
import { postHeadline, relativeTime } from "./postText";

const PREVIEW_COUNT = 3;

interface Props {
  club: ClubAPI.ClubDetail;
  onJoin: () => void;
  onSubscribe: () => void;
}

/**
 * The forum's entry point on the club page: latest discussions (pinned ones
 * are shown on the board above), a "New post" action and a link to the full forum.
 */
export function ClubForumPreview({ club, onJoin, onSubscribe }: Props) {
  const { t } = useT();
  const { access } = club;
  const posts = useClubPosts(club.slug, access.can.readForum);
  const [composerOpen, setComposerOpen] = useState(false);
  const forumUrl = `/clubs/${club.slug}/forum`;

  const latest = (posts.data?.pages[0]?.posts ?? []).filter((p) => !p.pinnedAt).slice(0, PREVIEW_COUNT);

  return (
    <section className="card p-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-start gap-3 p-4">
        <span className="w-11 h-11 rounded-xl bg-primary-500/15 text-primary-500 flex items-center justify-center flex-shrink-0">
          <MessagesSquare className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold leading-tight">{t("clubs.forum")}</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("clubs.forumDescription")}</p>
        </div>
        {access.can.writeForum && (
          <button onClick={() => setComposerOpen(true)} className="btn btn-primary btn-sm rounded-full gap-1.5 flex-shrink-0">
            <PenSquare className="w-4 h-4" />
            <span className="hidden sm:inline">{t("clubs.newPost")}</span>
          </button>
        )}
      </div>

      {!access.can.readForum ? (
        <div className="px-4 pb-4">
          <GateNotice
            message={access.isMember ? t("clubs.subscribeToPost") : t("clubs.joinToParticipate")}
            actionLabel={access.isMember ? t("clubs.subscribe") : t("clubs.join")}
            onAction={access.isMember ? onSubscribe : onJoin}
          />
        </div>
      ) : (
        <>
          {/* Latest discussions */}
          {posts.isLoading ? (
            <div className="px-4 pb-4 space-y-3">
              {Array.from({ length: PREVIEW_COUNT }).map((_, i) => (
                <div key={i} className="h-11 rounded-lg bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
              ))}
            </div>
          ) : latest.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-neutral-500 dark:text-neutral-400">{t("clubs.noPosts")}</p>
          ) : (
            <ul className="border-t border-black/5 dark:border-white/10 divide-y divide-black/5 dark:divide-white/10">
              {latest.map((post) => (
                <li key={post.id}>
                  <Link
                    href={`/clubs/${club.slug}/posts/${post.id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <ClubAuthorAvatar post={post} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{postHeadline(post)}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                        {post.isAdminPost ? t("clubs.outclose") : post.author?.firstName ?? t("clubs.deletedUser")} ·{" "}
                        {relativeTime(post.createdAt)}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400 flex-shrink-0">
                      <MessageCircle className="w-3.5 h-3.5" /> {post.replyCount}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {/* Footer */}
          <Link
            href={forumUrl}
            className="flex items-center justify-between px-4 py-3 border-t border-black/5 dark:border-white/10 text-sm font-medium text-primary-500 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
          >
            {t("clubs.seeAllDiscussions")}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </>
      )}

      {access.can.writeForum && (
        <ClubNewPostSheet isOpen={composerOpen} onClose={() => setComposerOpen(false)} slug={club.slug} clubName={club.name} />
      )}
    </section>
  );
}
